import moment from 'jalali-moment';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Swal from 'sweetalert2';
import * as XLSX from 'xlsx';
import endpoints from '../../config/endpoints';
import globalEnum from '../../enums/globalEnum';
import { hide } from '../../services/reducers/popupSlice';
import restApi from '../../services/restApi';
import { AppDispatch, useAppSelector } from '../../services/store';

import userRole = globalEnum.userRole;

const SOURCE_LABELS: Record<string, string> = {
  manual: 'افزایش دستی',
  order: 'سفارش',
  conversion: 'تبدیل رول به کارتن',
  correction: 'اصلاح از فرم محصول',
};

const UNIT_LABELS: Record<string, string> = {
  box: 'کارتن قوطی',
  roll: 'کارتن رولی',
};

interface CardexModalProps {
  product: any;
  // Called with the product returned by a rollback, so the Inventory page's
  // boxCount/rollCount/inventory totals stay in sync after a delete.
  onRollback?: (updatedProduct: any) => void;
}

// Full inventory ledger for one product — every stock movement regardless of
// source (manual additions, order deductions/restores, roll→box conversions,
// product-form corrections), unlike the "تاریخچه افزایش موجودی" table on the
// موجودی تب which only shows manual additions.
const CardexModal = ({ product, onRollback }: CardexModalProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const userReducer = useAppSelector(state => state.userReducer);
  const isSuperAdmin = userReducer?.profile?.role === userRole.superAdmin;
  const [logs, setLogs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [savingId, setSavingId] = useState<number | null>(null);

  const close = () => dispatch(hide());

  // Local edit buffer so typing doesn't refetch/re-render the whole list —
  // committed to the backend only on blur.
  const editDescription = (id: number, value: string) => {
    setLogs(prev => prev.map(log => (log.id === id ? { ...log, description: value } : log)));
  };

  const saveDescription = async (log: any) => {
    setSavingId(log.id);
    const res = await restApi(endpoints.product.cardexDescription(log.id), true).put({
      description: log.description || null,
    });
    setSavingId(null);
    if (res.code !== 200) {
      Swal.fire({ title: 'ناموفق', text: res?.data || 'خطا در ذخیره توضیحات', icon: 'error', confirmButtonText: 'متوجه شدم' });
    }
  };

  const deleteAndRollback = async (log: any) => {
    const confirm = await Swal.fire({
      title: 'حذف ردیف کاردکس',
      text: `این ردیف حذف می‌شود و اثر آن روی موجودی «${product.title}» برگردانده می‌شود (${log.isEntry ? '-' : '+'}${log.count} ${UNIT_LABELS[log.unitType] || log.unitType}). این عملیات قابل بازگشت نیست. ادامه می‌دهید؟`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'بله، حذف و برگردان',
      cancelButtonText: 'انصراف',
    });
    if (!confirm.isConfirmed) return;

    const res = await restApi(endpoints.product.cardexDelete(log.id), true).delete({});
    if (res.code === 200) {
      setLogs(prev => prev.filter(l => l.id !== log.id));
      onRollback?.(res.data);
      Swal.fire({ title: 'موفق', text: 'ردیف حذف و موجودی برگردانده شد', icon: 'success', confirmButtonText: 'متوجه شدم' });
    } else {
      Swal.fire({ title: 'ناموفق', text: res?.data || 'خطا در حذف/برگرداندن ردیف', icon: 'error', confirmButtonText: 'متوجه شدم' });
    }
  };

  const exportExcel = () => {
    const rows = logs.map(log => ({
      'تاریخ': moment(log.createdAt).format('jYYYY/jMM/jDD HH:mm'),
      'منبع': SOURCE_LABELS[log.source] || log.source || '—',
      'نوع': UNIT_LABELS[log.unitType] || log.unitType,
      'تغییر': (log.isEntry ? '+' : '-') + log.count,
      'تعداد در واحد': log.itemsPerUnit ?? '—',
      'موجودی قبل': log.oldInventory ?? '—',
      'موجودی بعد': log.newInventory ?? '—',
      'کاربر': log.user?.name || '—',
      'توضیحات': log.description || '—',
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'کاردکس');
    XLSX.writeFile(wb, `cardex-${product.sku || product.id}.xlsx`);
  };

  useEffect(() => {
    (async () => {
      const res = await restApi(endpoints.product.cardex + product.id, true).get();
      if (res.code === 200) setLogs(res.data || []);
      setLoading(false);
    })();
  }, [product.id]);

  const columnCount = 9 + (isSuperAdmin ? 1 : 0);

  return (
    <div className="modalContainer" style={{ width: 960, height: 'auto', position: 'static', margin: 0 }}>
      <div className="modalHeading">
        <h2>کاردکس — {product.title} ({product.sku})</h2>
        <i className="modalExit clickable" onClick={close}></i>
      </div>

      <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: 12 }}>
        <span className="secondaryBtn clickable" onClick={exportExcel}>⬇ خروجی اکسل</span>
      </div>

      <div className="cardex-table-container" style={{ maxHeight: 480, overflowY: 'auto' }}>
        <table className="cardex-table">
          <thead>
          <tr className="cardex-table-header">
            <th>تاریخ</th>
            <th>منبع</th>
            <th>نوع</th>
            <th>تغییر</th>
            <th>تعداد × واحد</th>
            <th>موجودی قبل</th>
            <th>موجودی بعد</th>
            <th>کاربر</th>
            <th>توضیحات</th>
            {isSuperAdmin && <th>عملیات</th>}
          </tr>
          </thead>
          <tbody>
          {logs.map((log) => (
            <tr className="cardex-row" key={log.id}>
              <td className="cardex-cell">{moment(log.createdAt).format('jYYYY/jMM/jDD HH:mm')}</td>
              <td className="cardex-cell">{SOURCE_LABELS[log.source] || log.source || '—'}</td>
              <td className="cardex-cell">{UNIT_LABELS[log.unitType] || log.unitType}</td>
              <td className="cardex-cell">
                <span className={log.isEntry ? 'cardex-entry' : 'cardex-exit'}>
                  {log.isEntry ? '+' : '-'}{log.count}
                </span>
              </td>
              <td className="cardex-cell">{log.count} × {log.itemsPerUnit ?? '—'}</td>
              <td className="cardex-cell">{log.oldInventory ?? '—'}</td>
              <td className="cardex-cell">{log.newInventory ?? '—'}</td>
              <td className="cardex-cell">{log.user?.name || '—'}</td>
              <td className="cardex-cell">
                {isSuperAdmin ? (
                  <input
                    type="text"
                    placeholder="یادداشت..."
                    value={log.description || ''}
                    disabled={savingId === log.id}
                    onChange={(e) => editDescription(log.id, e.target.value)}
                    onBlur={() => saveDescription(log)}
                    style={{ width: 160 }}
                  />
                ) : (log.description || '—')}
              </td>
              {isSuperAdmin && (
                <td className="cardex-cell">
                  {log.source === 'manual' && (
                    <i
                      className="trash clickable"
                      title="حذف و برگرداندن موجودی"
                      onClick={() => deleteAndRollback(log)}
                    ></i>
                  )}
                </td>
              )}
            </tr>
          ))}
          {!loading && logs.length === 0 && (
            <tr><td className="cardex-cell" colSpan={columnCount}>سابقه‌ای برای این محصول یافت نشد</td></tr>
          )}
          </tbody>
        </table>
      </div>

      <div className="modalBtns">
        <span className="cancel clickable" onClick={close}>بستن</span>
      </div>
    </div>
  );
};

export default CardexModal;
